import React from "react";
import { Card, Button, Form, InputGroup, Modal } from "react-bootstrap";
import { FormControlLabel, Checkbox } from "@material-ui/core";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  eachDayOfInterval,
  nextMonday,
  nextSunday,
  startOfMonth,
  endOfMonth,
  addMonths,
  isPast,
} from "date-fns";
import moment from "moment";
import { RESERVATION_COLOR } from "../../constants";
import { toPesoAmount } from "../../helpers";
import { formatDateRange } from "./utils";

const getNextWeek = () =>
  eachDayOfInterval({
    start: nextMonday(new Date()),
    end: nextSunday(nextMonday(new Date())),
  }).map((d) => d.toISOString());

const getNextMonth = () =>
  eachDayOfInterval({
    start: startOfMonth(addMonths(new Date(), 1)),
    end: endOfMonth(addMonths(new Date(), 1)),
  }).map((d) => d.toISOString());

export default class ExperienceManager extends React.Component {
  render() {
    const {
      experiences,
      isFormVisible,
      editingExperience,
      onAddNewClick,
      onEdit,
      onDelete,
      onSave,
      onCancel,
    } = this.props;
    return (
      <div className="d-flex flex-column mt-4">
        {/* List of existing experiences */}
        {experiences.map((experience) => (
          <Card
            key={experience._id}
            className="mb-5 shadow-sm border-0"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Body className="p-0">
              <div className="d-flex flex-column flex-sm-row align-items-sm-stretch">
                {/* Price Display */}
                <div
                  className="d-flex flex-column justify-content-center align-items-center p-6 bg-light"
                  style={{
                    minWidth: "140px",
                    borderBottom: "1px dashed #ebedf3",
                    borderRight: "none",
                  }}
                >
                  {/* Added d-flex flex-column align-items-center to force strict vertical stacking */}
                  <div className="d-flex flex-column align-items-center w-sm-110px text-center">
                    <span
                      className="text-muted font-weight-boldest font-size-xs mb-1 text-uppercase"
                      style={{ letterSpacing: "0.5px" }}
                    >
                      Experience Price
                    </span>

                    <span
                      className="text-dark font-weight-boldest font-size-h4"
                      style={{ color: RESERVATION_COLOR }}
                    >
                      {toPesoAmount(experience.price)}
                    </span>

                    {experience.isExclusive && (
                      <span
                        /* Changed ml-2 (margin-left) to mt-2 (margin-top) for vertical spacing */
                        className="badge badge-primary font-weight-boldest mt-2"
                        style={{
                          backgroundColor: RESERVATION_COLOR,
                          fontSize: "0.7rem",
                        }}
                      >
                        EXCLUSIVE
                      </span>
                    )}
                  </div>
                </div>
                {/* Content Area */}
                <div
                  className="flex-grow-1 p-6 border-sm-left"
                  style={{ borderLeft: "none" }}
                >
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex align-items-center mb-2">
                      <h4 className="text-dark-75 font-weight-boldest m-0">
                        {experience.name}
                      </h4>
                    </div>
                    <p
                      className="text-muted font-size-sm mb-4 line-height-md flex-grow-1"
                      style={{ maxWidth: "400px" }}
                    >
                      {experience.description || "No description provided."}
                    </p>

                    <div className="d-flex align-items-center flex-wrap">
                      {experience.available_days &&
                        experience.available_days.length > 0 && (
                          <div
                            className="d-inline-flex align-items-center px-3 py-1 rounded-pill mr-3"
                            style={{
                              backgroundColor: "rgba(137, 80, 252, 0.08)",
                              color: RESERVATION_COLOR,
                            }}
                          >
                            <i
                              className="far fa-calendar-alt mr-2 font-size-sm"
                              style={{ color: RESERVATION_COLOR }}
                            />
                            <span className="font-weight-boldest font-size-xs text-uppercase">
                              {formatDateRange(experience.available_days)}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="d-flex flex-row flex-sm-column justify-content-center p-4 border-top border-sm-top-0 border-sm-left bg-light-o-5">
                  <Button
                    variant="link"
                    className="btn btn-icon btn-light-info btn-sm mb-0 mb-sm-2 mr-2 mr-sm-0 shadow-xs"
                    onClick={() => onEdit(experience)}
                  >
                    <i
                      className="far fa-edit text-info font-size-h6"
                      style={{ color: RESERVATION_COLOR }}
                    />
                  </Button>
                  <Button
                    variant="link"
                    className="btn btn-icon btn-light-danger btn-sm shadow-xs"
                    onClick={() => onDelete(experience._id)}
                  >
                    <i className="far fa-trash-alt text-danger font-size-h6" />
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}

        {isFormVisible ? (
          <ExperienceForm
            experience={editingExperience}
            onSave={onSave}
            onCancel={onCancel}
          />
        ) : (
          <Button
            variant="outline-info"
            className="w-100 p-8 mt-2 d-flex flex-column align-items-center justify-content-center"
            onClick={onAddNewClick}
            style={{
              border: `2px dashed ${RESERVATION_COLOR}`,
              backgroundColor: "rgba(137, 80, 252, 0.03)",
              borderRadius: "12px",
              transition: "all 0.3s ease",
            }}
          >
            <i
              className="fas fa-plus-circle mb-3"
              style={{ fontSize: "2rem", color: RESERVATION_COLOR }}
            />
            <span
              className="font-weight-boldest text-uppercase"
              style={{
                color: RESERVATION_COLOR,
                letterSpacing: "1.5px",
                fontSize: "0.9rem",
              }}
            >
              Add New Experience
            </span>
          </Button>
        )}
      </div>
    );
  }
}

class ExperienceForm extends React.Component {
  constructor(props) {
    super(props);
    this.calendarRef = React.createRef();

    this.state = {
      formData: this.props.experience || {
        name: "",
        description: "",
        price: "",
        isExclusive: false,
      },
      isRollingReservation:
        !this.props.experience || !this.props.experience.available_days?.length,
      showCalendarModal: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.experience !== prevProps.experience) {
      this.setState({
        formData: this.props.experience || {
          name: "",
          description: "",
          price: "",
          isExclusive: false,
        },
        isRollingReservation:
          !this.props.experience ||
          !this.props.experience.available_days?.length,
      });
    }
  }

  setAvailableDates(dates) {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        available_days: dates,
      },
    }));
  }

  handleDateClick = (arg) => {
    const clickedDate = arg.dateStr;
    if (
      isPast(new Date(clickedDate)) &&
      !moment(clickedDate).isSame(moment(), "day")
    )
      return;

    this.setState((prevState) => {
      const currentDates = prevState.formData.available_days || [];
      const newDates = currentDates.includes(clickedDate)
        ? currentDates.filter((d) => d !== clickedDate)
        : [...currentDates, clickedDate];

      return { formData: { ...prevState.formData, available_days: newDates } };
    });
  };

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.onSave(this.state.formData);
  };

  render() {
    const { isRollingReservation, showCalendarModal } = this.state;
    return (
      <Card
        className="mt-5 border-0 shadow-sm"
        style={{ borderRadius: "12px" }}
      >
        <Card.Body className="p-8">
          <div className="d-flex align-items-center mb-8">
            <span
              className="btn btn-icon btn-light-info mr-4 no-hover"
              style={{ cursor: "default" }}
            >
              <i className="fas fa-edit text-info"></i>
            </span>
            <h3 className="text-dark font-weight-boldest m-0">
              {this.props.experience && this.props.experience._id
                ? "Edit Experience Details"
                : "Add New Experience"}
            </h3>
          </div>

          <Form onSubmit={this.handleSubmit}>
            <div className="row">
              <div className="col-md-8">
                <Form.Group className="mb-6">
                  <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-2">
                    Experience Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    className="form-control-solid"
                    placeholder="e.g. VIP Dinner, Wine Tasting"
                    value={this.state.formData.name}
                    onChange={this.handleChange}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-6">
                  <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-2">
                    Price (PHP)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    className="form-control-solid"
                    placeholder="0.00"
                    value={this.state.formData.price}
                    onChange={this.handleChange}
                    required
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-6">
              <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-2">
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                className="form-control-solid"
                placeholder="Give your customers a glimpse of what to expect..."
                value={this.state.formData.description}
                onChange={this.handleChange}
                maxLength="200"
              />
              <div className="d-flex justify-content-end mt-2">
                <span className="badge badge-light font-weight-boldest text-muted">
                  {(this.state.formData.description || "").length} / 200
                </span>
              </div>
            </Form.Group>

            <div className="bg-light-o-5 rounded-xl mb-4">
              {/* Replaced flex-stack with justify-content-between and added w-100 */}
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="d-flex flex-column mr-5">
                  <span className="text-dark font-weight-boldest font-size-lg mb-1">
                    Exclusive Experience?
                  </span>
                  <span className="text-muted font-size-sm">
                    Mark this experience as exclusive.
                  </span>
                </div>

                {/* Added ml-auto (or ms-auto for BS5) as an extra guarantee to push it right */}
                <span className="switch switch-outline switch-icon switch-info ml-auto">
                  <label className="m-0">
                    <input
                      type="checkbox"
                      name="isExclusive"
                      checked={this.state.formData.isExclusive || false}
                      onChange={this.handleChange}
                    />
                    <span></span>
                  </label>
                </span>
              </div>
            </div>

            <div className="bg-light-o-5 rounded-xl mb-4">
              <div className="d-flex flex-stack align-items-center">
                <div className="d-flex flex-column mr-5">
                  <span className="text-dark font-weight-boldest font-size-lg mb-1">
                    Rolling Reservation?
                  </span>
                  <span className="text-muted font-size-sm">
                    Allow customers to book any day, or restrict to specific
                    dates. Note: <b>Rolling Reservations </b>are subject to the
                    assigned table's availability.
                  </span>
                </div>
                <span className="switch switch-outline switch-icon switch-info">
                  <label className="m-0">
                    <input
                      type="checkbox"
                      checked={isRollingReservation}
                      onChange={() =>
                        this.setState({
                          isRollingReservation: !isRollingReservation,
                          formData: {
                            ...this.state.formData,
                            available_days: [],
                          },
                        })
                      }
                    />
                    <span></span>
                  </label>
                </span>
              </div>
            </div>

            {!isRollingReservation && (
              <div className="mb-8 p-6 border-info border-dashed rounded-xl border-2">
                <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-4 d-block">
                  Available Dates Selection
                </Form.Label>
                <div className="d-flex flex-wrap mb-4">
                  <Button
                    variant="light"
                    className="btn btn-sm btn-light-info font-weight-boldest mr-2 mb-2 px-6"
                    onClick={() => this.setAvailableDates(getNextWeek())}
                  >
                    NEXT WEEK
                  </Button>
                  <Button
                    variant="light"
                    className="btn btn-sm btn-light-info font-weight-boldest mr-2 mb-2 px-6"
                    onClick={() => this.setAvailableDates(getNextMonth())}
                  >
                    NEXT MONTH
                  </Button>
                  <Button
                    variant="light"
                    className="btn btn-sm btn-light-primary font-weight-boldest mb-2 px-6"
                    onClick={() => this.setState({ showCalendarModal: true })}
                    style={{
                      backgroundColor: "rgba(137, 80, 252, 0.1)",
                      color: RESERVATION_COLOR,
                    }}
                  >
                    CUSTOM DATES
                  </Button>
                </div>
                {this.state.formData.available_days &&
                  this.state.formData.available_days.length > 0 && (
                    <div
                      className="d-flex align-items-center bg-white p-4 rounded shadow-xs"
                      style={{ borderLeft: `4px solid ${RESERVATION_COLOR}` }}
                    >
                      <i
                        className="far fa-calendar-check mr-3 text-info font-size-h3"
                        style={{ color: RESERVATION_COLOR }}
                      />
                      <div className="d-flex flex-column">
                        <span className="text-muted font-weight-bold font-size-xs">
                          SELECTED RANGE
                        </span>
                        <span className="text-dark font-weight-boldest font-size-sm">
                          {formatDateRange(this.state.formData.available_days)}
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            )}

            <div className="d-flex justify-content-end border-top pt-8">
              <Button
                variant="light"
                className="btn btn-light-info font-weight-boldest px-8 mr-3"
                onClick={this.props.onCancel}
              >
                CANCEL
              </Button>
              <Button
                type="submit"
                className="btn btn-info font-weight-boldest px-8 shadow-sm"
                style={{ backgroundColor: RESERVATION_COLOR }}
              >
                SAVE EXPERIENCE
              </Button>
            </div>
          </Form>

          <Modal
            show={showCalendarModal}
            onHide={() => this.setState({ showCalendarModal: false })}
            centered
            onEnter={() => {
              setTimeout(() => {
                this.calendarRef.current?.getApi().updateSize();
              }, 100);
            }}
            size="xl"
          >
            <Modal.Header closeButton className="border-0">
              <Modal.Title className="font-weight-boldest">
                Select Available Dates
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0">
              <div
                className="events-calendar-wrapper"
                style={{
                  padding: "20px",
                  borderRadius: "15px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <FullCalendar
                  height={550}
                  ref={this.calendarRef}
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  dateClick={this.handleDateClick}
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,dayGridWeek",
                  }}
                  events={this.state.formData.available_days?.map((date) => ({
                    start: date,
                    display: "background",
                    backgroundColor: RESERVATION_COLOR,
                  }))}
                />
              </div>
              <div className="d-flex justify-content-end mt-6">
                <Button
                  className="btn btn-info font-weight-boldest px-10"
                  style={{ backgroundColor: RESERVATION_COLOR }}
                  onClick={() => this.setState({ showCalendarModal: false })}
                >
                  DONE SELECTING
                </Button>
              </div>
            </Modal.Body>
          </Modal>
        </Card.Body>
      </Card>
    );
  }
}
